import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Users, Award, TrendingUp, CheckCircle, Factory, Building2, Zap, Shield, Truck, Clock, Phone, Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { companyInfo, productCategories, industries } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';

// Testimonials data
const testimonials = [
  {
    id: 1,
    company: 'PT. Krakatau Steel',
    person: 'Budi Santoso',
    position: 'Procurement Manager',
    contentId: 'Gurita Mandala Persada telah menjadi mitra terpercaya kami selama lebih dari 8 tahun. Keahlian teknis dan kualitas produk mereka tidak tertandingi.',
    contentEn: 'Gurita Mandala Persada has been our trusted partner for over 8 years. Their technical expertise and product quality are unmatched.',
    rating: 5
  },
  {
    id: 2,
    company: 'PT. Rimba Insantek Mandiri',
    person: '-',
    position: 'Project Director',
    contentId: 'Pengiriman yang andal, kualitas konsisten, dan dukungan teknis yang sangat baik. Mereka memahami tuntutan proyek berskala besar.',
    contentEn: 'Reliable delivery, consistent quality, and excellent technical support. They understand the demands of large-scale projects.',
    rating: 5
  },
  {
    id: 3,
    company: 'PT. Indonesia Power',
    person: 'Ahmad Wijaya',
    position: 'Engineering Lead',
    contentId: 'Jangkauan produk dan pengetahuan teknis mereka telah menjadikan mereka mitra utama kami untuk semua kebutuhan pasokan industri.',
    contentEn: 'Their product range and technical knowledge have made them our go-to partner for all industrial supply requirements.',
    rating: 5
  }
];

// Partner logos
const partnerLogos = [
  { name: 'Spindo', logo: '/assets/suppliers/spindo.png' },
  { name: 'Hamasa', logo: '/assets/suppliers/hamasa.png' },
  { name: 'BKR', logo: '/assets/suppliers/bkr.png' },
  { name: 'Sutindo', logo: '/assets/suppliers/sutindo.png' },
  { name: 'Krakatau Steel', logo: '/assets/suppliers/ks.png' },
  { name: 'Catur Prima Perkasa', logo: '/assets/suppliers/cpp.png' },
  { name: 'Putra Prima', logo: '/assets/suppliers/pp.png' },
];

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { t, language } = useLanguage();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const productTranslations = [
    { key: 'valves', nameKey: 'productCategories.valves', descKey: 'productCategories.valvesDesc' },
    { key: 'pipes', nameKey: 'productCategories.pipes', descKey: 'productCategories.pipesDesc' },
    { key: 'fittings', nameKey: 'productCategories.fittings', descKey: 'productCategories.fittingsDesc' },
    { key: 'steel', nameKey: 'productCategories.steelMaterials', descKey: 'productCategories.steelMaterialsDesc' }
  ];

  const industryTranslations = [
    'industryNames.manufacturing',
    'industryNames.oilGas',
    'industryNames.construction',
    'industryNames.infrastructure',
    'industryNames.powerUtilities'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden pt-20">
        {/* Animated background grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(0deg, transparent 24%, rgba(6, 147, 227, .1) 25%, rgba(6, 147, 227, .1) 26%, transparent 27%, transparent 74%, rgba(6, 147, 227, .1) 75%, rgba(6, 147, 227, .1) 76%, transparent 77%, transparent), linear-gradient(90deg, transparent 24%, rgba(6, 147, 227, .1) 25%, rgba(6, 147, 227, .1) 26%, transparent 27%, transparent 74%, rgba(6, 147, 227, .1) 75%, rgba(6, 147, 227, .1) 76%, transparent 77%, transparent)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                {t('home.heroTitle')}{' '}
                <span className="text-[#0693E3]">{t('home.heroTitleHighlight')}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {t('home.heroSubtitle', { years: companyInfo.yearsOfExperience })}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/products">
                  <Button size="lg" className="bg-[#0693E3] hover:bg-[#0578BE] text-white text-base px-8 py-6">
                    {t('home.viewProducts')}
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-2 border-[#CF2E2E] text-[#CF2E2E] hover:bg-[#CF2E2E] hover:text-white text-base px-8 py-6">
                    {t('home.requestQuotation')}
                  </Button>
                </Link>
              </div>
            </div>

            <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjU4NjAwOTV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Modern Industrial Facility"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="text-[#0693E3]" size={24} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{companyInfo.projectsCompleted}+</div>
                      <div className="text-sm text-gray-600">{t('home.projectsCompleted')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Metrics Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Award className="text-[#0693E3]" size={32} />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{companyInfo.yearsOfExperience}+</div>
              <div className="text-gray-600 font-medium">{t('home.yearsExperience')}</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <Users className="text-[#CF2E2E]" size={32} />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{companyInfo.industriesServed}+</div>
              <div className="text-gray-600 font-medium">{t('home.industriesServed')}</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Package className="text-[#0693E3]" size={32} />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">7</div>
              <div className="text-gray-600 font-medium">{t('home.productCategories')}</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <TrendingUp className="text-[#CF2E2E]" size={32} />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{companyInfo.projectsCompleted}+</div>
              <div className="text-gray-600 font-medium">{t('home.projectsCompleted')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services/Features Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              {language === 'id' ? 'Layanan Unggulan Kami' : 'Our Premium Services'}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {language === 'id' 
                ? 'Komitmen kami untuk memberikan solusi terbaik bagi kebutuhan industri Anda'
                : 'Our commitment to delivering the best solutions for your industrial needs'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl hover:bg-white/20 transition-all group">
              <div className="w-14 h-14 bg-[#0693E3] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Truck size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">
                {language === 'id' ? 'Pengiriman Cepat' : 'Fast Delivery'}
              </h3>
              <p className="text-gray-400">
                {language === 'id' 
                  ? 'Layanan pengiriman cepat ke seluruh Indonesia dengan armada logistik terpercaya'
                  : 'Fast delivery service throughout Indonesia with trusted logistics fleet'}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl hover:bg-white/20 transition-all group">
              <div className="w-14 h-14 bg-[#CF2E2E] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">
                {language === 'id' ? 'Jaminan Kualitas' : 'Quality Guarantee'}
              </h3>
              <p className="text-gray-400">
                {language === 'id' 
                  ? 'Semua produk telah melalui quality control ketat dan bersertifikasi'
                  : 'All products undergo strict quality control and are certified'}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl hover:bg-white/20 transition-all group">
              <div className="w-14 h-14 bg-[#0693E3] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Clock size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">
                {language === 'id' ? 'Respons 24 Jam' : '24-Hour Response'}
              </h3>
              <p className="text-gray-400">
                {language === 'id' 
                  ? 'Tim kami siap merespons permintaan penawaran dalam waktu 24 jam'
                  : 'Our team is ready to respond to quotation requests within 24 hours'}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl hover:bg-white/20 transition-all group">
              <div className="w-14 h-14 bg-[#CF2E2E] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Phone size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">
                {language === 'id' ? 'Konsultasi Gratis' : 'Free Consultation'}
              </h3>
              <p className="text-gray-400">
                {language === 'id' 
                  ? 'Dapatkan konsultasi teknis gratis dari tim ahli kami'
                  : 'Get free technical consultation from our expert team'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Preview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.productRangeTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.productRangeSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productCategories.map((category, index) => {
              const translation = productTranslations[index];
              return (
                <Card key={category.id} className="overflow-hidden group hover:shadow-xl transition-all duration-300 border-0">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">
                      {t(translation.nameKey)}
                    </h3>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">{t(translation.descKey)}</p>
                    <Link to="/products">
                      <Button variant="outline" className="w-full group-hover:bg-[#0693E3] group-hover:text-white group-hover:border-[#0693E3] transition-colors">
                        {t('home.viewDetails')}
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-[#0693E3] hover:bg-[#0578BE] text-white">
                {t('home.viewAllProducts')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.whyPartnerTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.whyPartnerSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
                <CheckCircle className="text-[#0693E3]" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.provenReliability')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('home.provenReliabilityDesc')}
              </p>
            </div>

            <div className="text-center p-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
                <Factory className="text-[#CF2E2E]" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.technicalExpertise')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('home.technicalExpertiseDesc')}
              </p>
            </div>

            <div className="text-center p-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
                <Zap className="text-[#0693E3]" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.qualityAssurance')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('home.qualityAssuranceDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' ? 'Apa Kata Pelanggan Kami' : 'What Our Customers Say'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'id' 
                ? 'Kepercayaan pelanggan adalah prioritas utama kami'
                : 'Customer trust is our top priority'}
            </p>
          </div>

          <div className="max-w-4xl mx-auto relative">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <Quote className="text-[#0693E3] mb-6" size={48} />
              
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8">
                {language === 'id' 
                  ? testimonials[currentTestimonial].contentId 
                  : testimonials[currentTestimonial].contentEn}
              </p>

              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center mb-2">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={20} />
                    ))}
                  </div>
                  <p className="font-bold text-gray-900 text-lg">{testimonials[currentTestimonial].person}</p>
                  <p className="text-gray-600">{testimonials[currentTestimonial].position}</p>
                  <p className="text-[#0693E3] font-medium">{testimonials[currentTestimonial].company}</p>
                </div>

                <div className="flex space-x-2">
                  <button 
                    onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-[#0693E3] hover:text-white flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button 
                    onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-[#0693E3] hover:text-white flex items-center justify-center transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* Testimonial indicators */}
            <div className="flex justify-center space-x-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentTestimonial ? 'bg-[#0693E3]' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos Section */}
      <section className="py-16 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {language === 'id' ? 'Dipercaya Oleh' : 'Trusted By'}
            </h2>
          </div>
        </div>
        
        {/* Marquee Container */}
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10" />
          
          <div className="flex w-max animate-marquee-home">
            {[...partnerLogos, ...partnerLogos].map((partner, index) => (
              <div 
                key={index} 
                className="flex-shrink-0 mx-8 flex items-center justify-center"
                style={{ minWidth: '150px', height: '80px' }}
              >
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="max-h-10 max-w-[120px] object-contain filter grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-gray-500 font-semibold text-sm">${partner.name}</span>`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes marquee-home {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee-home {
            animation: marquee-home 25s linear infinite;
          }
          .animate-marquee-home:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* Industries Preview */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t('home.industriesTitle')}</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('home.industriesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {industries.map((industry, index) => (
              <div key={industry.id} className="bg-gray-800 p-6 rounded-lg text-center hover:bg-gray-700 transition-colors">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0693E3] rounded-full mb-4">
                  <Building2 className="text-white" size={32} />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t(industryTranslations[index])}</h3>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/industries">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900">
                {t('home.learnMore')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0693E3]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('home.ctaSubtitle')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#CF2E2E] hover:bg-[#B02525] text-white text-lg px-10 py-6">
              {t('home.ctaButton')}
              <ArrowRight className="ml-2" size={24} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
